<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useDocumentVisibility, useWindowFocus, useWindowSize } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { AnimatePresence, motion } from 'motion-v'
import { useChatStore } from '@/config/store/chatStore'
import ConversationThread from '@/components/messaging/ConversationThread.vue'
import ConversationMembersModal from '@/components/messaging/ConversationMembersModal.vue'
import ChatSettingsModal from '@/components/messaging/ChatSettingsModal.vue'
import CreateConversationModal from '@/components/messaging/CreateConversationModal.vue'
import ResourceImage from '@/components/resource/resourceImage.vue'
import Loading from '@/components/common/loading.vue'
import TextInput from '@/components/common/textInput.vue'
import MaterialSymbolsChatOutlineRounded from '~icons/material-symbols/chat-outline-rounded'
import MaterialSymbolsCloseRounded from '~icons/material-symbols/close-rounded'
import MaterialSymbolsRemoveRounded from '~icons/material-symbols/remove-rounded'
import MaterialSymbolsOpenInFullRounded from '~icons/material-symbols/open-in-full-rounded'
import MaterialSymbolsArrowBackRounded from '~icons/material-symbols/arrow-back-rounded'
import MaterialSymbolsGroupRounded from '~icons/material-symbols/group-rounded'
import MaterialSymbolsVisibilityOffRounded from '~icons/material-symbols/visibility-off-rounded'
import MaterialSymbolsSearchRounded from '~icons/material-symbols/search-rounded'
import MaterialSymbolsSettingsOutlineRounded from '~icons/material-symbols/settings-outline-rounded'
import MaterialSymbolsAddRounded from '~icons/material-symbols/add-rounded'
import type { ChatMessage } from '@/services/chat/chatHttp.service'
import { displayLocale } from '@/services/application/displayLocale'

/** Same frost recipe as BExpandablePill — light needs lower alpha than dark to stay readable as glass */
const GLASS_CLASS = 'bg-white/50 dark:bg-[#141E28]/75 backdrop-blur-xl backdrop-saturate-150'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { width } = useWindowSize()
const visibility = useDocumentVisibility()
const windowFocused = useWindowFocus()
const chatStore = useChatStore()

const {
  canAccess,
  unreadCount,
  conversations,
  isLoadingConversations,
  widgetHidden,
  widgetExpanded,
  widgetConversationId,
  widgetConversation,
} = storeToRefs(chatStore)

const threadRef = ref<InstanceType<typeof ConversationThread> | null>(null)
const threadSearchOpen = ref(false)
const threadSearchQuery = ref('')
const showMembersModal = ref(false)
const showSettings = ref(false)
const showCreateModal = ref(false)
const navDirection = ref<'forward' | 'back'>('forward')

/** Narrow viewport: almost-fullscreen sheet; desktop keeps the corner panel */
const isMobileViewport = computed(() => width.value < 1024)
const isOnMessagingPage = computed(() => route.name === 'Messaging')
const showWidget = computed(() =>
  canAccess.value
  && !isOnMessagingPage.value
  && !widgetHidden.value,
)
/** On mobile the header already has minimize — hide FAB so it does not cover the composer */
const showFab = computed(() => showWidget.value && !(isMobileViewport.value && widgetExpanded.value))

const isPageActive = computed(() =>
  visibility.value === 'visible' && windowFocused.value,
)

const timeFormatter = new Intl.DateTimeFormat(displayLocale.value, {
  hour: '2-digit',
  minute: '2-digit',
})
const dateFormatter = new Intl.DateTimeFormat(displayLocale.value, {
  month: 'short',
  day: 'numeric',
})

const formatDate = (date: Date | string | null) => {
  if (!date) return ''
  const d = date instanceof Date ? date : new Date(date)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) return timeFormatter.format(d)
  return dateFormatter.format(d)
}

watch(showWidget, (visible) => {
  if (visible) {
    chatStore.refreshConversations()
  }
}, { immediate: true })

watch(isPageActive, (active) => {
  if (active && widgetExpanded.value && widgetConversationId.value) {
    threadRef.value?.markReadIfActive?.()
  }
})

const stopListening = chatStore.onEvent((event) => {
  if (!widgetConversationId.value || !threadRef.value) return
  if (event.event === 'message.created' && event.payload.conversationId === widgetConversationId.value) {
    threadRef.value.appendMessage(event.payload.message)
  }
  else if ((event.event === 'message.updated' || event.event === 'message.deleted')
    && event.payload.conversationId === widgetConversationId.value) {
    threadRef.value.updateMessage(event.payload.message)
  }
  else if (event.event === 'reaction.updated'
    && event.payload.conversationId === widgetConversationId.value) {
    threadRef.value.updateReactions(event.payload.messageId, event.payload.reactions)
  }
})

onUnmounted(() => {
  stopListening()
})

const openConversation = (id: string) => {
  navDirection.value = 'forward'
  threadSearchOpen.value = false
  threadSearchQuery.value = ''
  chatStore.openConversationInWidget(id)
}

const onConversationCreated = async (conversation: { _id: string }) => {
  await chatStore.refreshConversations()
  openConversation(conversation._id)
}

const toggleThreadSearch = () => {
  threadSearchOpen.value = !threadSearchOpen.value
  if (!threadSearchOpen.value) {
    threadSearchQuery.value = ''
  }
}

const onBackFromThread = () => {
  navDirection.value = 'back'
  threadSearchOpen.value = false
  threadSearchQuery.value = ''
  chatStore.backToConversationList()
}

watch(widgetConversationId, () => {
  threadSearchOpen.value = false
  threadSearchQuery.value = ''
  showMembersModal.value = false
})

const onMessageSent = (_message: ChatMessage) => {
  chatStore.refreshConversations()
  if (widgetConversationId.value) {
    chatStore.markLocalRead(widgetConversationId.value)
  }
}

const openFullPage = () => {
  const conversationId = widgetConversationId.value
  chatStore.collapseWidget()
  router.push({
    name: 'Messaging',
    query: conversationId ? { conversationId } : {},
  })
}

const toggleBubble = () => {
  if (widgetExpanded.value) chatStore.collapseWidget()
  else chatStore.openWidget()
}

const iconTransition = {
  initial: { opacity: 0, scale: 0.7, rotate: -8 },
  animate: { opacity: 1, scale: 1, rotate: 0 },
  exit: { opacity: 0, scale: 0.7, rotate: 8 },
  transition: { duration: 0.12, ease: 'easeOut' },
}

const viewSpring = {
  type: 'spring' as const,
  stiffness: 420,
  damping: 32,
  mass: 0.75,
}

const listMotion = computed(() => ({
  initial: navDirection.value === 'back'
    ? { x: -20, opacity: 0 }
    : { x: 0, opacity: 1 },
  animate: { x: 0, opacity: 1 },
  exit: { x: -20, opacity: 0 },
  transition: viewSpring,
}))

const threadMotion = computed(() => ({
  initial: navDirection.value === 'forward'
    ? { x: 24, opacity: 0 }
    : { x: 0, opacity: 1 },
  animate: { x: 0, opacity: 1 },
  exit: { x: 24, opacity: 0 },
  transition: viewSpring,
}))
</script>

<template>
  <Teleport to="body">
    <Transition name="chat-frost-backdrop">
      <button
        v-if="showWidget && widgetExpanded && isMobileViewport"
        key="chat-backdrop"
        type="button"
        class="fixed inset-0 z-60 bg-black/40 print:hidden"
        :aria-label="t('modules.chat.minimize')"
        @click="chatStore.collapseWidget()"
      />
    </Transition>

    <!-- Fixed + no opacity transition on the glass node (opacity on same node kills backdrop-filter) -->
    <Transition name="chat-frost">
      <div
        v-if="showWidget && widgetExpanded"
        key="chat-panel"
        class="fixed z-60 border border-black/10 dark:border-white/10 shadow-2xl flex flex-col print:hidden overflow-hidden
 left-2 right-2 top-auto bottom-[max(0.5rem,env(safe-area-inset-bottom))] rounded-2xl
          h-[min(36rem,calc(100dvh-5rem))] max-h-[calc(100dvh-5rem)]
          lg:left-auto lg:right-5 lg:bottom-21 lg:w-[440px] xl:w-[520px] 2xl:w-[600px]
          lg:h-[min(680px,78vh)] lg:max-h-none xl:h-[min(760px,82vh)] 2xl:h-[min(840px,85vh)]"
        data-cy="chat-floating-panel"
        :class="GLASS_CLASS"
      >
        <div class="min-h-12 px-3 py-1.5 shrink-0 flex items-center gap-2 border-b dark:border-white/10">
          <button
            v-if="widgetConversationId"
            type="button"
            class="p-1.5 rounded-lg bg-hover active:scale-95 transition-transform"
            data-cy="chat-widget-back"
            :title="t('modules.chat.backToList')"
            @click="onBackFromThread"
          >
            <MaterialSymbolsArrowBackRounded class="w-5 h-5" />
          </button>
          <ResourceImage
            v-if="widgetConversation?.type === 'dm' && widgetConversation.otherParticipant && !threadSearchOpen"
            :resource="widgetConversation.otherParticipant"
            width="w-8"
            height="h-8"
            disable-link
          />
          <div
            v-else-if="widgetConversationId && !threadSearchOpen"
            class="w-8 h-8 shrink-0 rounded-full bg-primary-500/20 flex items-center justify-center"
          >
            <MaterialSymbolsGroupRounded class="w-4 h-4" />
          </div>
          <div
            v-if="!threadSearchOpen"
            class="min-w-0 flex-1"
          >
            <div class="font-semibold text-sm truncate">
              {{ widgetConversation?.title || t('modules.chat.title') }}
            </div>
            <button
              v-if="widgetConversation?.type === 'group'"
              type="button"
              class="text-[11px] opacity-60 hover:opacity-100 hover:text-primary-500 transition-opacity text-left"
              :title="t('modules.chat.viewMembers')"
              @click="showMembersModal = true"
            >
              {{ t('modules.chat.memberCount', { count: widgetConversation.memberCount }) }}
            </button>
          </div>
          <div
            v-else
            class="min-w-0 flex-1"
          >
            <TextInput
              v-model="threadSearchQuery"
              class="w-full"
              size="small"
              shadow=""
              autofocus
              :placeholder="t('modules.chat.searchInConversation')"
            >
              <template #prepend-icon>
                <MaterialSymbolsSearchRounded class="w-4 h-4 opacity-60" />
              </template>
            </TextInput>
          </div>
          <button
            v-if="widgetConversationId"
            type="button"
            class="p-1.5 rounded-lg bg-hover active:scale-95 transition-transform"
            data-cy="chat-widget-search-toggle"
            :title="threadSearchOpen ? t('common.cancel') : t('modules.chat.searchInConversation')"
            :aria-pressed="threadSearchOpen"
            @click="toggleThreadSearch"
          >
            <MaterialSymbolsCloseRounded
              v-if="threadSearchOpen"
              class="w-5 h-5"
            />
            <MaterialSymbolsSearchRounded
              v-else
              class="w-5 h-5"
            />
          </button>
          <button
            v-if="!widgetConversationId"
            type="button"
            class="p-1.5 rounded-lg bg-hover active:scale-95 transition-transform"
            data-cy="chat-widget-settings-button"
            :title="t('modules.chat.settings.open')"
            @click="showSettings = true"
          >
            <MaterialSymbolsSettingsOutlineRounded class="w-4 h-4" />
          </button>
          <button
            v-if="!widgetConversationId"
            type="button"
            class="p-1.5 rounded-lg bg-hover active:scale-95 transition-transform"
            data-cy="chat-widget-create-button"
            :title="t('modules.chat.newConversation')"
            @click="showCreateModal = true"
          >
            <MaterialSymbolsAddRounded class="w-5 h-5" />
          </button>
          <button
            type="button"
            class="p-1.5 rounded-lg bg-hover active:scale-95 transition-transform"
            data-cy="chat-widget-open-fullpage"
            :title="t('modules.chat.openFullPage')"
            @click="openFullPage"
          >
            <MaterialSymbolsOpenInFullRounded class="w-4 h-4" />
          </button>
          <button
            type="button"
            class="p-1.5 rounded-lg bg-hover active:scale-95 transition-transform"
            data-cy="chat-widget-minimize"
            :title="t('modules.chat.minimize')"
            @click="chatStore.collapseWidget()"
          >
            <MaterialSymbolsRemoveRounded class="w-5 h-5" />
          </button>
          <button
            type="button"
            class="p-1.5 rounded-lg bg-hover active:scale-95 transition-transform"
            data-cy="chat-widget-hide"
            :title="t('modules.chat.hideWidget')"
            @click="chatStore.hideWidget()"
          >
            <MaterialSymbolsVisibilityOffRounded class="w-4 h-4" />
          </button>
        </div>

        <div class="relative flex-1 min-h-0 overflow-hidden">
          <AnimatePresence mode="popLayout">
            <motion.div
              v-if="!widgetConversationId"
              key="conversation-list"
              class="absolute inset-0 overflow-y-auto mini-scroll"
              :initial="listMotion.initial"
              :animate="listMotion.animate"
              :exit="listMotion.exit"
              :transition="listMotion.transition"
            >
              <Loading
                v-if="isLoadingConversations && !conversations.length"
                class="p-4"
              />
              <div
                v-else-if="!conversations.length"
                class="p-6 text-center text-sm opacity-60"
              >
                {{ t('modules.chat.emptyConversations') }}
              </div>
              <div
                v-else
                class="flex flex-col gap-1 p-2"
              >
                <button
                  v-for="item in conversations"
                  :key="item._id"
                  type="button"
                  class="w-full text-left px-2.5 py-2 flex gap-3 items-start rounded-lg hover:bg-hover transition-colors active:scale-[0.99]"
                  :data-cy="`chat-widget-conversation-${item._id}`"
                  :data-conversation-title="item.title"
                  @click="openConversation(item._id)"
                >
                  <ResourceImage
                    v-if="item.type === 'dm' && item.otherParticipant"
                    :resource="item.otherParticipant"
                    width="w-9"
                    height="h-9"
                    disable-link
                  />
                  <div
                    v-else
                    class="w-9 h-9 shrink-0 rounded-full bg-primary-500/20 flex items-center justify-center"
                  >
                    <MaterialSymbolsGroupRounded class="w-4 h-4" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center justify-between gap-2">
                      <span class="font-medium text-sm truncate">{{ item.title }}</span>
                      <span class="text-[10px] opacity-50 shrink-0">{{ formatDate(item.lastMessageAt) }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-2 mt-0.5">
                      <span class="text-xs opacity-60 truncate">{{ item.lastMessagePreview || '—' }}</span>
                      <span
                        v-if="item.unreadCount > 0"
                        class="shrink-0 min-w-[1.15rem] px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center"
                      >
                        {{ item.unreadCount > 99 ? '99+' : item.unreadCount }}
                      </span>
                    </div>
                  </div>
                </button>
              </div>
            </motion.div>

            <motion.div
              v-else
              key="conversation-thread"
              class="absolute inset-0"
              :initial="threadMotion.initial"
              :animate="threadMotion.animate"
              :exit="threadMotion.exit"
              :transition="threadMotion.transition"
            >
              <ConversationThread
                ref="threadRef"
                v-model:search-query="threadSearchQuery"
                v-model:show-search="threadSearchOpen"
                :conversation="widgetConversation"
                compact
                @message-sent="onMessageSent"
                @marked-read="chatStore.markLocalRead(widgetConversationId!)"
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Transition>

    <div
      v-if="showFab"
      class="fixed z-60 bottom-[max(1.25rem,env(safe-area-inset-bottom))] right-5 print:hidden"
    >
      <motion.button
        type="button"
        class="relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center active:scale-95 transition-[background-color,color,box-shadow] duration-150"
        data-cy="chat-floating-fab"
        :class="widgetExpanded
 ? `${GLASS_CLASS} border border-black/10 dark:border-white/10`
          : 'bg-primary-500 text-white hover:brightness-110 border border-transparent'"
        :title="widgetExpanded ? t('modules.chat.minimize') : t('modules.chat.title')"
        :initial="{ scale: 0.9 }"
        :animate="{ scale: 1 }"
        :transition="{ duration: 0.15 }"
        @click="toggleBubble"
      >
        <AnimatePresence mode="popLayout">
          <motion.span
            :key="widgetExpanded ? 'close' : 'open'"
            class="absolute inset-0 flex items-center justify-center"
            :initial="iconTransition.initial"
            :animate="iconTransition.animate"
            :exit="iconTransition.exit"
            :transition="iconTransition.transition"
          >
            <MaterialSymbolsCloseRounded
              v-if="widgetExpanded"
              class="w-6 h-6"
            />
            <MaterialSymbolsChatOutlineRounded
              v-else
              class="w-7 h-7"
            />
          </motion.span>
        </AnimatePresence>
        <motion.span
          v-if="!widgetExpanded && unreadCount > 0"
          class="absolute -top-0.5 -right-0.5 z-10 min-w-5 h-5 px-1 rounded-full bg-white text-red-600 text-[11px] font-bold flex items-center justify-center shadow-md ring-2 ring-primary-500"
          data-cy="chat-floating-unread"
          :initial="{ scale: 0 }"
          :animate="{ scale: 1 }"
          :transition="{ type: 'spring', stiffness: 500, damping: 22 }"
        >
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </motion.span>
      </motion.button>
    </div>

    <ConversationMembersModal
      v-model="showMembersModal"
      :conversation-id="widgetConversationId"
    />
    <ChatSettingsModal v-model="showSettings" />
    <CreateConversationModal
      v-model="showCreateModal"
      @created="onConversationCreated"
    />
  </Teleport>
</template>

<style>
/* No opacity on the glass node — it disables backdrop-filter in Chromium */
.chat-frost-enter-active,
.chat-frost-leave-active {
  transition: transform 0.18s ease-out;
}
.chat-frost-enter-from,
.chat-frost-leave-to {
  transform: translateY(10px) scale(0.98);
}

.chat-frost-backdrop-enter-active,
.chat-frost-backdrop-leave-active {
  transition: opacity 0.18s ease-out;
}
.chat-frost-backdrop-enter-from,
.chat-frost-backdrop-leave-to {
  opacity: 0;
}
</style>
